import React from 'react';
import { Box, Container, Typography, List, ListItem, ListItemText, Divider } from '@mui/material';

const TermsPage: React.FC = () => {
  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', py: { xs: 6, md: 10 } }}>
      <Container maxWidth="md" sx={{ backgroundColor: 'white', borderRadius: 3, p: { xs: 3, md: 6 }, boxShadow: '0 12px 40px rgba(15,23,42,0.08)' }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
          TenantTerms Terms &amp; Legal Notice
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
          Effective Date: {new Date().getFullYear()}
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          1. What We Are (and Are Not)
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
          TenantTerms is a self-help platform that generates template lease language based on inputs you provide. We are not a law firm, broker, property manager, insurance provider, credit agency, or collection service. No attorney-client or fiduciary relationship is created by using the platform, and we do not represent any party to your lease.
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          2. Not Legal Advice; Use at Your Own Risk
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
          Platform content, checklists, and generated clauses are for informational purposes only. They may not reflect the latest federal, state, county, or city requirements, may omit disclosures unique to your property, and may not be enforceable in your jurisdiction. You should consult a licensed attorney to review any lease before you sign or rely on it. You are solely responsible for deciding whether any document is appropriate for your situation.
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          3. Your Responsibilities
        </Typography>
        <List dense sx={{ mb: 2, color: 'text.secondary', pl: 2 }}>
          <ListItem disableGutters>
            <ListItemText primary="Verify that required disclosures, notices, and addenda (e.g., rent caps, habitability, lead paint, mold, asbestos, bed bugs, flood risk, fire sprinklers, parking, utilities) are included and accurate for your property and location." />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText primary="Confirm all monetary amounts, timelines, late fees, deposit rules, and renewal terms before sharing, signing, or collecting any payment." />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText primary="Comply with fair housing, consumer privacy, data security, rent control/just cause, habitability, short-term rental, HOA/CC&R, and any other applicable laws." />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText primary="Provide accurate inputs and promptly update them if circumstances change; you are responsible for any errors or omissions in information you supply." />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText primary="Seek advice from qualified counsel or other licensed professionals when you need legal, tax, accounting, insurance, or compliance guidance." />
          </ListItem>
        </List>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          4. Lease Outputs and Platform Limits
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
          Generated language is a starting point only. We do not guarantee completeness, enforceability, suitability, or accuracy for your specific property, tenants, or transaction. We are not a party to any lease or dispute, do not collect or hold payments, and do not guarantee tenant performance, rent payments, background screenings, or credit outcomes. Electronic signatures or document delivery tools are provided on an as-is basis and you are responsible for validating that signatures and notices comply with applicable law.
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          5. Third-Party Services and Content
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
          The platform may reference third-party forms, data, calculators, or integrations. We do not control, endorse, or warrant any third-party service, and you must review their terms and privacy notices. Any reliance on third-party information or links is at your own risk.
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          6. Acceptable Use
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
          Do not use TenantTerms to violate any law, discriminate, harass, misrepresent facts, scrape or reverse engineer the service, or infringe the rights of others. You may share templates for informational purposes but may not market them as legal advice or guaranteed-compliant documents.
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          7. Disclaimers of Warranties
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE PLATFORM AND ALL CONTENT ARE PROVIDED “AS IS” AND “AS AVAILABLE,” WITHOUT ANY WARRANTY OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING WARRANTIES OF ACCURACY, COMPLETENESS, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR ERROR-FREE OPERATION.
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          8. Limitation of Liability
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
          TO THE FULLEST EXTENT PERMITTED BY LAW, TENANTTERMS AND ITS OWNERS, EMPLOYEES, AND CONTRACTORS ARE NOT LIABLE FOR ANY INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOST PROFITS, REVENUE, DATA, GOODWILL, OR ANTICIPATED SAVINGS. OUR TOTAL LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THE PLATFORM IS LIMITED TO THE GREATER OF (A) THE AMOUNT YOU PAID US FOR THE SERVICE IN THE TWELVE (12) MONTHS BEFORE THE CLAIM AROSE OR (B) ONE HUNDRED DOLLARS ($100). YOUR SOLE AND EXCLUSIVE REMEDY FOR DISSATISFACTION WITH THE PLATFORM IS TO STOP USING IT.
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          9. Indemnification
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
          You agree to defend, indemnify, and hold TenantTerms harmless from any claims, losses, liabilities, damages, costs, and expenses (including reasonable attorneys’ fees) arising out of or related to your use of the platform, your documents, your inputs or omissions, or your violation of these terms or applicable law.
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          10. Changes, Governing Law, and Disputes
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
          We may update the platform and these terms at any time; continued use means you accept the updated terms. Review the latest version before relying on any document. These terms are governed by the laws of the State of California, without regard to conflict-of-law rules. Any disputes will be resolved in the state or federal courts located in Los Angeles County, California, and you consent to that jurisdiction and venue.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
          Questions? Email us at <a href="mailto:tenantterms@gmail.com">tenantterms@gmail.com</a>.
        </Typography>
      </Container>
    </Box>
  );
};

export default TermsPage;
